"use client";

import WertWidget from "@wert-io/widget-initializer";
import type { Options } from "@wert-io/widget-initializer/types";
import { useWallet } from "@getpara/react-sdk";
import { ChainId } from "@lifi/sdk";
import { PublicKey } from "@solana/web3.js";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { Address, encodeFunctionData, zeroAddress } from "viem";
import { toast } from "sonner";

import { CardProcessor, processorAddresses } from "@/constants";

// Payment status listener for Wert widget - shows notification when purchase completes
const createPaymentStatusListeners = (): Options["listeners"] => ({
  "payment-status": (data) => {
    // Wert statuses: 'pending', 'success', 'failed', 'cancelled'
    if (data.status === "success" || data.status === "pending") {
      toast.success(
        "Your tokens are on the way! Credit card purchases may take 5-10 minutes to appear in your wallet. Please be patient.",
        {
          duration: 10000, // 10 seconds
        }
      );
    }
  },
});
import { useParaAccount } from "@/hooks/use-appkit-account";
import { usePortfolioStore } from "@/components/wallet/mobile-portfolio-toggle";
import { useCreditCardBuyStore } from "@/lib/zustand";
import { createWertSolanaIx } from "@/lib/wert-solana-ix";
import { SOLANA_PROGRAM_ID } from "@/lib/solana";
import { dataToHexString } from "@/services";

type PaymentMethod = "apple-pay" | "google-pay";

type OpenWertOnRampParams = {
  chainId: number;
  network: string;
  commodity: string;
  usdAmount: number;
  tokenAddress?: string;
  paymentMethod?: PaymentMethod;
  onStart?: () => void;
  onFinally?: () => void;
};

const isValidSolanaAddress = (addr: string | null | undefined): boolean => {
  if (!addr) return false;
  if (addr.startsWith("0x")) return false;
  return addr.length >= 32 && addr.length <= 44;
};

export function useWertOnramp() {
  const { address } = useParaAccount();
  const { data: paraWallet } = useWallet();
  const { publicKey: solanaPublicKey } = useSolanaWallet();
  const { setIsOpen: setPortfolioOpen } = usePortfolioStore();
  const { setIsWertWidgetOpen } = useCreditCardBuyStore();

  const CARD_PROCESSOR_ADDRESSES: Record<number, Address> =
    processorAddresses as Record<number, Address>;

  const fetchPriceUsd = async (
    commodity: string,
    chainId: number
  ): Promise<number> => {
    const dexScreenerAddresses: Record<string, { chain: string; address: string }> = {
      SOL: {
        chain: "solana",
        address: "So11111111111111111111111111111111111111112",
      },
      ETH: {
        chain: "ethereum",
        address: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
      },
      BNB: { chain: "bsc", address: "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c" },
    };

    const coinbaseSymbols: Record<string, string> = {
      SOL: "SOL-USD",
      ETH: "ETH-USD",
      BNB: "BNB-USD",
    };

    const binanceSymbols: Record<string, string> = {
      SOL: "SOLUSDT",
      ETH: "ETHUSDT",
      BNB: "BNBUSDT",
    };

    void chainId;

    try {
      const dex = dexScreenerAddresses[commodity];
      if (dex) {
        const res = await fetch(
          `https://api.dexscreener.com/tokens/v1/${dex.chain}/${dex.address}`,
          { headers: { Accept: "application/json" } }
        );
        if (res.ok) {
          const pairs: any[] = await res.json();
          const price = Number(pairs?.[0]?.priceUsd || 0);
          if (price > 0) return price;
        }
      }
    } catch {}

    try {
      const symbol = coinbaseSymbols[commodity];
      if (symbol) {
        const res = await fetch(`https://api.coinbase.com/v2/prices/${symbol}/spot`, {
          headers: { Accept: "application/json" },
        });
        if (res.ok) {
          const data = await res.json();
          const price = Number(data?.data?.amount || 0);
          if (price > 0) return price;
        }
      }
    } catch {}

    try {
      const symbol = binanceSymbols[commodity];
      if (symbol) {
        const res = await fetch(
          `https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`,
          { headers: { Accept: "application/json" } }
        );
        if (res.ok) {
          const data = await res.json();
          const price = Number(data?.price || 0);
          if (price > 0) return price;
        }
      }
    } catch {}

    throw new Error(
      `Unable to fetch live price for ${commodity} — all price sources failed`
    );
  };

  const calculateTokenAmount = async (
    commodity: string,
    chainId: number,
    usdAmount: number
  ): Promise<number> => {
    const price = await fetchPriceUsd(commodity, chainId);
    return usdAmount / price;
  };

  const getSignature = async (
    commodity: string,
    network: string,
    contract_address: string,
    buyAmount: number,
    data: string,
    recipientAddress?: string
  ) => {
    const signatureAddress =
      recipientAddress || address || "0x3f1b0334ae9c405D94f330c9460D98079349f2aC";

    const url = `/api/secure-route?address=${signatureAddress}&buyAmount=${buyAmount}&network=${network}&commodity=${commodity}&contractAddress=${contract_address}&inputData=${data}`;

    const resp = await fetch(url);

    if (resp.ok) {
      const data = await resp.json();
      return data;
    }
    return null;
  };

  const openWertOnRamp = async ({
    chainId,
    network,
    commodity,
    usdAmount,
    tokenAddress,
    paymentMethod,
    onStart,
    onFinally,
  }: OpenWertOnRampParams) => {
    onStart?.();
    setIsWertWidgetOpen(true);
    setPortfolioOpen(false);

    try {
      const partnerID = process.env.NEXT_PUBLIC_WERT_PARTNER_ID_PRODUCTION;
      const origin = process.env.NEXT_PUBLIC_WERT_ORIGIN;

      const otherWidgetOptions: Options = {
        partner_id: partnerID || "",
        origin: origin || "https://widget.wert.io",
        theme: "dark",
        ...(paymentMethod ? { payment_method: paymentMethod } : {}),
      };

      const tokenAmount = await calculateTokenAmount(commodity, chainId, usdAmount);
      console.log("Live price fetch result:", tokenAmount, "for USD:", usdAmount);

      if (chainId === ChainId.SOL) {
        const solanaAddress =
          solanaPublicKey?.toBase58() ||
          (paraWallet?.type === "SOLANA" ? (paraWallet as any)?.address : null) ||
          (isValidSolanaAddress(address) ? address : null) ||
          null;

        console.log("Solana address resolution:", {
          solanaPublicKey: solanaPublicKey?.toBase58() ?? null,
          paraWalletType: (paraWallet as any)?.type ?? null,
          paraWalletAddress: (paraWallet as any)?.address ?? null,
          accountAddress: address ?? null,
          solanaAddress,
        });

        if (!solanaAddress) {
          toast.error("Please connect a Solana wallet");
          setIsWertWidgetOpen(false);
          return;
        }

        // Use 4 decimals for SOL to reduce USD display gap while maintaining sufficient precision
        const buyAmountForRoute = parseFloat(tokenAmount.toFixed(4));
        const targetMint =
          tokenAddress &&
          tokenAddress !== "11111111111111111111111111111111" &&
          tokenAddress !== "So11111111111111111111111111111111111111112"
            ? tokenAddress
            : "So11111111111111111111111111111111111111112";
        const allInstructions = createWertSolanaIx(
          solanaAddress,
          buyAmountForRoute,
          new PublicKey(targetMint),
          100
        );

        const wertScInputData = dataToHexString(allInstructions);
        const signature = await getSignature(
          commodity,
          network,
          SOLANA_PROGRAM_ID,
          buyAmountForRoute,
          wertScInputData,
          solanaAddress
        );

        if (!signature) {
          toast.error("Error getting signature");
          return;
        }

        // NOTE: When using Wert's smart contract widget, the displayed USD amount may
        // differ slightly (~0.01-0.05%) from the user's input. This is unavoidable —
        // the signed commodity_amount is cryptographically binding, and Wert recalculates
        // USD from that token amount at their current rate. The currency_amount field
        // is ignored in smart contract mode (verified against SDK source). The native
        // widget mode (openGeneralizedWidget in credit-card-form.tsx) shows exact USD
        // because it doesn't lock in a token amount upfront.
        const wertWidget = new WertWidget({
          ...signature,
          ...otherWidgetOptions,
          listeners: createPaymentStatusListeners(),
        });
        wertWidget.open();
      } else {
        if (!address || !address.startsWith("0x")) {
          toast.error("Please connect an EVM wallet for this chain");
          setIsWertWidgetOpen(false);
          return;
        }

        const buyAmountForRoute =
          Math.floor(tokenAmount * 100_000_000) / 100_000_000;

        const cardProcessorAddress = CARD_PROCESSOR_ADDRESSES[chainId];

        let contractAddressForSignature: string;
        let inputDataForSignature: string;

        if (cardProcessorAddress && address) {
          const token = (
            tokenAddress &&
            tokenAddress !== zeroAddress &&
            tokenAddress.startsWith("0x")
              ? tokenAddress
              : zeroAddress
          ) as Address;
          const router = zeroAddress as Address;
          const minOut = BigInt(0);
          const to = address as Address;

          inputDataForSignature = encodeFunctionData({
            abi: CardProcessor.abi,
            functionName: "buyToken",
            args: [token, router, minOut, to],
          });

          contractAddressForSignature = cardProcessorAddress as string;
        } else {
          contractAddressForSignature = zeroAddress;
          inputDataForSignature = "0x";
        }

        const signature = await getSignature(
          commodity,
          network,
          contractAddressForSignature,
          buyAmountForRoute,
          inputDataForSignature
        );

        if (!signature) {
          toast.error("Error getting signature");
          setIsWertWidgetOpen(false);
          return;
        }

        console.log("Wert EVM widget payload:", {
          commodity: signature.commodity,
          commodity_address: signature.commodity_address,
          network: signature.network,
          address: signature.address,
          sc_address: signature.sc_address,
          sc_input_data: signature.sc_input_data,
          commodity_amount: signature.commodity_amount,
        });

        const wertWidget = new WertWidget({
          ...signature,
          ...otherWidgetOptions,
          listeners: createPaymentStatusListeners(),
        });
        wertWidget.open();
      }
    } catch {
      toast.error("Failed to open payment widget");
    } finally {
      setIsWertWidgetOpen(false);
      onFinally?.();
    }
  };

  return { openWertOnRamp };
}
